/**
 * Export a chat conversation to a Word (.docx) file.
 *
 * Full markdown → native Word conversion:
 *   - Headings (#, ##, ###) → Word heading styles
 *   - **bold**, *italic*, `code` → native Word formatting
 *   - Markdown tables → native Word tables
 *   - Ordered / unordered lists → indented numbered / bulleted paragraphs
 *   - Code blocks (```) → monospaced shaded paragraphs
 *   - Blockquotes (>) → indented italic with left border
 */
import {
  Document,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  HeadingLevel,
  AlignmentType,
  BorderStyle,
  ShadingType,
  Packer,
  LevelFormat,
  convertInchesToTwip,
} from 'docx'
import { saveAs } from 'file-saver'
import type { ChatTurn } from './types'
import { DISCLAIMER_TEXTS } from './disclaimer'

// ────────────────────────── Inline formatting parser ─────────────────────────
// Splits a line of text into TextRun objects respecting **bold**, *italic*,
// ***bold+italic***, and `inline code`.

function parseInlineRuns(text: string, baseSize = 21): TextRun[] {
  const runs: TextRun[] = []
  // Regex order matters: bold+italic first, then bold, italic, code
  const re = /(\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`)/g
  let lastIdx = 0
  let m: RegExpExecArray | null

  while ((m = re.exec(text)) !== null) {
    // Text before this match
    if (m.index > lastIdx) {
      runs.push(new TextRun({ text: text.slice(lastIdx, m.index), size: baseSize }))
    }
    if (m[2] !== undefined) {
      // ***bold+italic***
      runs.push(new TextRun({ text: m[2], bold: true, italics: true, size: baseSize }))
    } else if (m[3] !== undefined) {
      // **bold**
      runs.push(new TextRun({ text: m[3], bold: true, size: baseSize }))
    } else if (m[4] !== undefined) {
      // *italic*
      runs.push(new TextRun({ text: m[4], italics: true, size: baseSize }))
    } else if (m[5] !== undefined) {
      // `code`
      runs.push(new TextRun({
        text: m[5],
        font: 'Consolas',
        size: baseSize - 1,
        shading: { type: ShadingType.CLEAR, color: 'auto', fill: 'F3F4F6' },
      }))
    }
    lastIdx = m.index + m[0].length
  }
  // Remaining text
  if (lastIdx < text.length) {
    runs.push(new TextRun({ text: text.slice(lastIdx), size: baseSize }))
  }
  if (runs.length === 0) {
    runs.push(new TextRun({ text, size: baseSize }))
  }
  return runs
}

// ─────────────────────── Markdown table parser ───────────────────────────────

function isTableSeparatorRow(line: string): boolean {
  return /^\|[\s:]*[-]+[\s:]*(\|[\s:]*[-]+[\s:]*)*\|?$/.test(line.trim())
}

function parseTableRow(line: string): string[] {
  return line.replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim())
}

function buildWordTable(rows: string[][]): Table {
  const wordRows = rows.map((cells, rowIdx) =>
    new TableRow({
      children: cells.map((cellText) =>
        new TableCell({
          children: [
            new Paragraph({
              children: parseInlineRuns(cellText, 20),
              spacing: { before: 40, after: 40 },
            }),
          ],
          shading: rowIdx === 0
            ? { type: ShadingType.CLEAR, color: 'auto', fill: 'EFF6FF' }
            : undefined,
        }),
      ),
    }),
  )

  return new Table({
    rows: wordRows,
    width: { size: 100, type: WidthType.PERCENTAGE },
  })
}

// ──────────────── Block-level markdown → docx elements ───────────────────────
// Returns an array of (Paragraph | Table) items for one markdown content block.

export type DocChild = Paragraph | Table

export function markdownToDocxElements(content: string): DocChild[] {
  const elements: DocChild[] = []
  const lines = content.split('\n')
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    // ── Empty line ──
    if (!line.trim()) {
      elements.push(new Paragraph({ spacing: { after: 80 } }))
      i++
      continue
    }

    // ── Code block (``` ... ```) ──
    if (line.trim().startsWith('```')) {
      const codeLines: string[] = []
      i++ // skip opening ```
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i])
        i++
      }
      i++ // skip closing ```
      for (const cl of codeLines) {
        elements.push(
          new Paragraph({
            children: [
              new TextRun({
                text: cl || ' ',
                font: 'Consolas',
                size: 19,
              }),
            ],
            shading: { type: ShadingType.CLEAR, color: 'auto', fill: 'F8F9FA' },
            indent: { left: 200 },
            spacing: { after: 20 },
          }),
        )
      }
      continue
    }

    // ── Markdown table ──
    // A table starts with a `|` line, followed by a separator `|---|---|`,
    // then data rows.
    if (line.trim().startsWith('|') && i + 1 < lines.length && isTableSeparatorRow(lines[i + 1])) {
      const tableRows: string[][] = []
      tableRows.push(parseTableRow(line)) // header
      i += 2 // skip header + separator
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableRows.push(parseTableRow(lines[i]))
        i++
      }
      elements.push(buildWordTable(tableRows))
      elements.push(new Paragraph({ spacing: { after: 120 } }))
      continue
    }

    // ── Headings ──
    const headingMatch = line.match(/^(#{1,4})\s+(.+)/)
    if (headingMatch) {
      const depth = headingMatch[1].length
      const level =
        depth === 1 ? HeadingLevel.HEADING_2
        : depth === 2 ? HeadingLevel.HEADING_3
        : HeadingLevel.HEADING_4
      elements.push(
        new Paragraph({
          children: parseInlineRuns(headingMatch[2], depth === 1 ? 28 : depth === 2 ? 24 : 22),
          heading: level,
          spacing: { before: 240, after: 120 },
        }),
      )
      i++
      continue
    }

    // ── Ordered list (1. ... / 2. ...) ──
    const olMatch = line.match(/^(\d+)\.\s+(.+)/)
    if (olMatch) {
      elements.push(
        new Paragraph({
          children: [
            new TextRun({ text: `${olMatch[1]}. `, bold: true, size: 21 }),
            ...parseInlineRuns(olMatch[2]),
          ],
          indent: { left: convertInchesToTwip(0.35) },
          spacing: { after: 60 },
        }),
      )
      i++
      continue
    }

    // ── Unordered list (- ... / * ...) ──
    const ulMatch = line.match(/^[-*]\s+(.+)/)
    if (ulMatch) {
      elements.push(
        new Paragraph({
          children: [
            new TextRun({ text: '•  ', size: 21 }),
            ...parseInlineRuns(ulMatch[1]),
          ],
          indent: { left: convertInchesToTwip(0.35) },
          spacing: { after: 60 },
        }),
      )
      i++
      continue
    }

    // ── Nested list (  - ... indented) ──
    const nestedUlMatch = line.match(/^\s{2,}[-*]\s+(.+)/)
    if (nestedUlMatch) {
      elements.push(
        new Paragraph({
          children: [
            new TextRun({ text: '◦  ', size: 21 }),
            ...parseInlineRuns(nestedUlMatch[1]),
          ],
          indent: { left: convertInchesToTwip(0.7) },
          spacing: { after: 50 },
        }),
      )
      i++
      continue
    }

    // ── Blockquote ──
    if (line.startsWith('>')) {
      const quoteText = line.replace(/^>\s*/, '')
      elements.push(
        new Paragraph({
          children: parseInlineRuns(quoteText),
          indent: { left: convertInchesToTwip(0.35) },
          border: {
            left: { style: BorderStyle.SINGLE, size: 6, color: '9CA3AF', space: 8 },
          },
          spacing: { after: 60 },
        }),
      )
      i++
      continue
    }

    // ── Horizontal rule (--- / ***) ──
    if (/^[-*_]{3,}\s*$/.test(line)) {
      elements.push(
        new Paragraph({
          border: {
            bottom: { style: BorderStyle.SINGLE, size: 1, color: 'D1D5DB' },
          },
          spacing: { before: 100, after: 100 },
        }),
      )
      i++
      continue
    }

    // ── Regular paragraph ──
    elements.push(
      new Paragraph({
        children: parseInlineRuns(line),
        spacing: { after: 80 },
      }),
    )
    i++
  }

  return elements
}

// ──────────────────────── Public API ──────────────────────────────────────────

interface ExportOptions {
  title: string
  caseNumber?: string
  turns: ChatTurn[]
  lang?: 'zh' | 'en'
}

export async function exportChatToDocx({
  title,
  caseNumber,
  turns,
  lang = 'zh',
}: ExportOptions) {
  const children: DocChild[] = []

  // ── Document title ──
  children.push(
    new Paragraph({
      text: title,
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
    }),
  )

  if (caseNumber) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `${lang === 'zh' ? '案件编号' : 'Case No.'}: ${caseNumber}`,
            size: 20,
            color: '666666',
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 },
      }),
    )
  }

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `${lang === 'zh' ? '导出时间' : 'Exported'}: ${new Date().toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-US')}`,
          size: 20,
          color: '666666',
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
    }),
  )

  children.push(
    new Paragraph({
      border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' } },
      spacing: { after: 300 },
    }),
  )

  // ── Conversation turns ──
  for (let i = 0; i < turns.length; i++) {
    const turn = turns[i]
    const isUser = turn.role === 'user'

    const roleLabel = isUser
      ? (lang === 'zh' ? '用户提问' : 'User Question')
      : (lang === 'zh' ? 'AI 回复' : 'AI Response')

    // Role header with colored background
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `【${roleLabel}】`,
            bold: true,
            size: 24,
            color: isUser ? '1D4ED8' : '1F2937',
          }),
        ],
        shading: isUser
          ? { type: ShadingType.CLEAR, color: 'auto', fill: 'EFF6FF' }
          : { type: ShadingType.CLEAR, color: 'auto', fill: 'F9FAFB' },
        spacing: { before: 360, after: 120 },
      }),
    )

    // Convert markdown content to Word elements
    const contentElements = markdownToDocxElements(turn.content)
    children.push(...contentElements)

    // Sources info
    if (!isUser && turn.sources && turn.sources.length > 0) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `📎 ${lang === 'zh' ? '参考来源' : 'Sources'}: ${turn.sources.length} ${lang === 'zh' ? '条' : 'items'}`,
              size: 18,
              color: '6B7280',
              italics: true,
            }),
          ],
          spacing: { before: 120, after: 80 },
        }),
      )
    }

    // Separator between turns
    if (i < turns.length - 1) {
      children.push(
        new Paragraph({
          border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: 'E5E7EB' } },
          spacing: { before: 200, after: 200 },
        }),
      )
    }
  }

  // ── Disclaimer (full version, appended after separator) ──
  const l = lang === 'zh' ? 'zh' : 'en'
  const disclaimerLines = DISCLAIMER_TEXTS.fullDisclaimer[l].split('\n')

  // Separator line
  children.push(
    new Paragraph({
      border: { bottom: { style: BorderStyle.SINGLE, size: 2, color: '999999' } },
      spacing: { before: 400, after: 200 },
    }),
  )

  // Disclaimer title (bold)
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: disclaimerLines[0],
          bold: true,
          size: 22,
        }),
      ],
      spacing: { before: 200, after: 120 },
    }),
  )

  // Disclaimer body paragraphs
  for (let i = 1; i < disclaimerLines.length; i++) {
    const line = disclaimerLines[i]
    if (!line.trim()) {
      children.push(new Paragraph({ spacing: { after: 80 } }))
    } else {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: line,
              size: 20,
            }),
          ],
          spacing: { after: 80 },
        }),
      )
    }
  }

  // ── Build & download ──
  const doc = new Document({
    numbering: {
      config: [{
        reference: 'ordered-list',
        levels: [{
          level: 0,
          format: LevelFormat.DECIMAL,
          text: '%1.',
          alignment: AlignmentType.LEFT,
        }],
      }],
    },
    sections: [{ children }],
  })

  const blob = await Packer.toBlob(doc)
  const filename = `${title.replace(/[/\\?%*:|"<>]/g, '_')}_${new Date().toISOString().slice(0, 10)}.docx`
  saveAs(blob, filename)
}

// ──────────────────── Document export (single doc) ───────────────────────────

interface DocExportOptions {
  title: string
  caseNumber?: string
  content: string
  lang?: 'zh' | 'en'
}

export async function exportDocToDocx({
  title,
  caseNumber,
  content,
  lang = 'zh',
}: DocExportOptions) {
  const children: DocChild[] = []

  children.push(
    new Paragraph({
      text: title,
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
    }),
  )

  if (caseNumber) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `${lang === 'zh' ? '案件编号' : 'Case No.'}: ${caseNumber}`,
            size: 20,
            color: '666666',
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 },
      }),
    )
  }

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `${lang === 'zh' ? '导出时间' : 'Exported'}: ${new Date().toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-US')}`,
          size: 20,
          color: '666666',
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
    }),
  )

  children.push(
    new Paragraph({
      border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' } },
      spacing: { after: 300 },
    }),
  )

  // Document content (markdown → docx)
  const contentElements = markdownToDocxElements(content)
  children.push(...contentElements)

  const doc = new Document({
    numbering: {
      config: [{
        reference: 'ordered-list',
        levels: [{
          level: 0,
          format: LevelFormat.DECIMAL,
          text: '%1.',
          alignment: AlignmentType.LEFT,
        }],
      }],
    },
    sections: [{ children }],
  })

  const blob = await Packer.toBlob(doc)
  const filename = `${title.replace(/[/\\?%*:|"<>]/g, '_')}_${new Date().toISOString().slice(0, 10)}.docx`
  saveAs(blob, filename)
}
